/* 请求封装 */
import axios, { type AxiosRequestConfig } from 'axios';
import { dealWarning } from './status';
import router from '@/router';
import { repoInfoStore } from '@/stores/Repo';
import { useGlobalInfoStore } from '@/stores/Global';
import { cutParamsInUrl } from './index';
import { getUtmSourceSign } from './cookieHandle';
import degradeInterceptor from './degradeInterceptor';
import isObject from 'lodash/isObject';
import { i18n } from '@/i18n';
import { ApiType, RepoType } from '@/utils/types';
import { useAccount } from '@/utils/hooks/useAccount';
import { PASSPORT_PREFIX } from '@/constant';
import { getCommonHeadInfo } from '@/utils/chatHeaderInfo';
import { VITE_API_HOST, VITE_DEV_API_HOST, VITE_MAIN_API_HOST, VITE_CHAT_API_HOST } from '@/utils/tools';
import { url403 } from '@/utils/code-transfer';
const { t } = i18n.global;

const handleError = dealWarning();

/** 设置用户中心相关api迁移时的api前缀 */
const setPassportPrefix = (url: string, method: string) => {
  const prefix = PASSPORT_PREFIX;
  if (prefix) {
    if (url?.includes('/api/v1/user/')) {
      // 用户信息相关
      return `${prefix}${url}`;
    }
    if (url?.includes('/api/v1/oauth/')) {
      // 用户权限
      return `${prefix}${url}`;
    }
    if (url?.includes('/api/v1/internal/messages')) {
      // 消息通知
      return `${prefix}${url}`;
    }
    if (url?.includes('/api/v1/follow')) {
      // 关注状态
      return `${prefix}${url}`;
    }
    if (url?.includes('/api/v1/obs') && method === 'get') {
      // 图片相关
      return `${prefix}${url}`;
    }
  }
  return url;
};

export const baseURL = VITE_API_HOST();
export const baseDevURL = VITE_DEV_API_HOST();
export const baseMainURL = VITE_MAIN_API_HOST();
export const baseChatURL = VITE_CHAT_API_HOST();
type CustomConfigs = {
  customError?: boolean; // 是否使用自定义的错误处理
  customTimeout?: number; // 自定义超时时间
  ignoreError?: boolean; // 不提示任何报错
  customErrorCodes?: number[]; // 自定义错误码处理
};

interface PendingTask {
  config: AxiosRequestConfig;
  resolve: Function;
}
let refreshing = false;
const queue: PendingTask[] = [];

const refreshToken = async() => axios.request({
  url: baseURL + PASSPORT_PREFIX + '/api/v1/user/token/refresh',
  method: 'post',
  data: {
    refresh_token: localStorage.getItem('refresh_token')
  },
  headers: {
    Authorization: 'Bearer ' + localStorage.getItem('access_token'),
    'content-type': 'application/x-www-form-urlencoded'
  },
  withCredentials: true
});

const proxyService: <T>(params: any, customConfigs?: CustomConfigs) => Promise<T> = async (params: any, customConfigs?: CustomConfigs) => {
  const baseUrlConfig = {
    [ApiType.DEVPRESS]: baseDevURL,
    [ApiType.MAIN]: baseMainURL,
    [ApiType.CHAT]: baseChatURL
  };

  const notReportApiTypes = [ApiType.DEVPRESS, ApiType.CHAT];

  const baseUrl = baseUrlConfig[params.apiType] || baseURL;
  const service = axios.create({
    baseURL: baseUrl,
    timeout: typeof customConfigs?.customTimeout === 'number' ? customConfigs?.customTimeout : 30000,
    withCredentials: true
  });

  // 如果参数中包含 signal，直接传递给 axios（axios 支持 AbortController 的 signal 参数）
  /** user api相关请求白名单 */
  const whiteList = ['/login', 'login/mobile', '/sendVeriCode', '/register', '/quickLogin', '/quickRegister', '/forgetCode', '/forgetPassword', '/checkSameUser', '/token'];

  const notAuthorization = (url: string = '') => {
    return whiteList.some((str) => url.includes(str));
  };

  // request 拦截器
  service.interceptors.request.use(
    (config) => {
      // 配置请求头
      const globalStore = useGlobalInfoStore();
      const repoStore = repoInfoStore();
      const currentRoute = router?.currentRoute?.value;
      const repoId = currentRoute?.meta?.type === 'repo' ? repoStore.repoInfo?.id : undefined;
      const currentType = repoStore.repoInfo?.repo_type;
      const reportTypeMap = {
        [RepoType.model]: 'AI社区模型详情页',
        [RepoType.dataset]: 'AI社区数据集详情页',
        [RepoType.spaces]: 'AI社区Space详情页'
      };
      // 在路由beforeEach中上报获取不到meta时，使用window.page_title
      const commonReportTitle = currentRoute?.meta?.reportTitle || window.page_title;
      const title = reportTypeMap[currentType] || config.headers['homeweb-page-title'] || commonReportTitle;
      delete config.headers['homeweb-page-title'];
      if (title && !notReportApiTypes.includes(params.apiType)) {
        // devpress接口不需要上报
        config.headers['page-title'] = encodeURIComponent(title);
      }
      if (currentRoute.name === 'homepage' && !notReportApiTypes.includes(params.apiType)) {
        const homeTitleConfig = {
          0: '个人主页',
          1: '组织主页'
        };
        const pageTitle = homeTitleConfig[globalStore.namespaceType as 0 | 1];
        config.headers['page-title'] = encodeURIComponent(pageTitle);
      }
      if (repoId && !notReportApiTypes.includes(params.apiType)) {
        config.headers['page-repo-id'] = repoId;
      }

      if (!notReportApiTypes.includes(params.apiType)) {
        config.headers['page-ref'] = config.headers?.['page-ref'] || encodeURIComponent(cutParamsInUrl(window.page_ref || ''));
        config.headers['page-uri'] = config.headers?.['page-uri'] || encodeURIComponent(cutParamsInUrl(window.location.href || ''));
        config.headers['gitcode-utm-source'] = config.headers?.['gitcode-utm-source'] || encodeURIComponent(getUtmSourceSign());
      }
      Object.assign(config.headers, {
        ...getCommonHeadInfo(params.apiType === ApiType.CHAT)
      });

      const access_token = window.localStorage.getItem('access_token');
      const { url } = config;
      if (access_token && !notAuthorization(url)) {
        config.headers.Authorization = `Bearer ${access_token}`;
      }
      // 修改用户模块前缀名称
      config.url = setPassportPrefix(config.url as string, config.method as string);
      // GET 请求中 ref 参数统一替换为 ref_replace_web
      if (config.method?.toLowerCase() === 'get' && config.params && typeof config.params === 'object' && 'ref' in config.params) {
        config.params.ref_replace_web = config.params.ref;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  service.interceptors.request.use(degradeInterceptor.onRequestFulfilled, degradeInterceptor.onRequestRejected);

  // 添加403情况下的token刷新时间缓存管理
  const TOKEN_REFRESH_403_KEY = 'token_refresh_403_timestamp';
  const REFRESH_INTERVAL = 23 * 60 * 60 * 1000 + 55 * 60 * 1000; // 23小时55分钟

  const shouldRefreshTokenFor403 = (url: string): boolean => {
    // 检查URL是否匹配正则表达式
    const matchesPattern = url403.some(pattern => pattern.test(url));
    if (!matchesPattern) return false;

    // 检查是否在时间限制内
    const lastRefreshTime = localStorage.getItem(TOKEN_REFRESH_403_KEY);
    if (!lastRefreshTime) return true;

    const timeDiff = Date.now() - parseInt(lastRefreshTime);
    return timeDiff > REFRESH_INTERVAL;
  };

  const recordTokenRefresh403 = (): void => {
    localStorage.setItem(TOKEN_REFRESH_403_KEY, Date.now().toString());
  };

  // response 拦截器（返回的 response 即 axios 收到的完整响应，headers 由浏览器暴露范围决定）
  // 若需在 JS 中读取 Content-Disposition 等非简单头，后端须返回：Access-Control-Expose-Headers: Content-Disposition
  service.interceptors.response.use(
    (response) => {
      return response;
    },
    async(error) => {
      const { response } = error;
      const { config } = response || {};
      if (refreshing) {
        return new Promise((resolve) => {
          queue.unshift({
            config,
            resolve
          });
        });
      }
      if (response?.status === 401 && localStorage.getItem('access_token') && !config?.url.includes('/token/refresh')) {
        const { RemoveInfo } = useAccount();
        const handleRefreshError = () => {
          RemoveInfo();
          location.reload();
          handleError({ res: {}, logout: true });
        };
        refreshing = true;
        try {
          const res = await refreshToken();
          refreshing = false;
          if (res.status === 200) {
            const { access_token, refresh_token } = res.data;
            localStorage.setItem('access_token', access_token);
            localStorage.setItem('refresh_token', refresh_token);
            queue.forEach(({ config, resolve }) => {
              if (config.headers) {
                config.headers.Authorization = `Bearer ${access_token}`;
              }
              resolve(axios(config));
            });
            queue.splice(0);
            config.headers.Authorization = `Bearer ${access_token}`;
            return axios(config);
          } else {
            handleRefreshError();
            return Promise.reject(res.data);
          }
        } catch (e) {
          handleRefreshError();
        }
      } else if (response) {
        // 当前错误为403并且是需要处理的接口也需要刷新token的接口 23小时55分一次
        if (response.status === 403 && shouldRefreshTokenFor403(config?.url || '')) {
          if (localStorage.getItem('access_token') && !config?.url.includes('/token/refresh')) {
            const { RemoveInfo } = useAccount();
            const handleRefreshError = () => {
              RemoveInfo();
              location.reload();
              handleError({ res: {}, logout: true });
            };
            if (!refreshing) {
              refreshing = true;
              try {
                const res = await refreshToken();
                refreshing = false;
                if (res.status === 200) {
                  const { access_token, refresh_token } = res.data;
                  localStorage.setItem('access_token', access_token);
                  localStorage.setItem('refresh_token', refresh_token);
                  recordTokenRefresh403(); // 记录刷新时间
                  queue.forEach(({ config, resolve }) => {
                    if (config.headers) {
                      config.headers.Authorization = `Bearer ${access_token}`;
                    }
                    resolve(axios(config));
                  });
                  queue.splice(0);
                  config.headers.Authorization = `Bearer ${access_token}`;
                  return axios(config);
                } else {
                  handleRefreshError();
                  return Promise.reject(res.data);
                }
              } catch (e) {
                handleRefreshError();
              }
            } else {
              return new Promise((resolve) => {
                queue.unshift({
                  config,
                  resolve
                });
              });
            }
          }
        }
        // 请求已发出，但是不在2xx的范围
        const errData = isObject(response?.data) ? response.data : {};
        errData.http_status = response.status;
        errData.message = error.message;
        if (!customConfigs || !customConfigs.customError) {
          handleError({
            res: response,
            logout: false,
            errorWay: params.errorWay,
            customErrorCodes: customConfigs?.customErrorCodes
          });
        }
        return Promise.reject(errData);
      } else {
        /**
         * 跨域（Network Error）
         * 网络连接超时导致请求取消（timeout of xxxms exceeded）
         */
        const timeoutRegex = /timeout/i;
        // const errorRegex = /network\s+error/i;
        if (error?.message && !customConfigs?.ignoreError) {
          // 未知错误
          // if (errorRegex.test(error.message)) {
          //   DNotificationService.open({
          //     content: t('common.network.unknowError')
          //   });
          // }
          // 超时错误
          if (timeoutRegex.test(error.message)) {
            // eslint-disable-next-line
            DMessage.warning(t('common.network.timeout'));
          }
        }
        return Promise.reject(error);
      }
    }
  );

  service.interceptors.response.use(degradeInterceptor.onResponseFulfilled, degradeInterceptor.onResponseRejected);

  return service({
    ...params,
    signal: params.signal // 确保 signal 参数被正确传递给 axios
  });
};

export default proxyService;

